import { LocalDate } from "@js-joda/core";
import { type GetTimeReportsV2SummaryResponse } from "services/api/company/timeReports.service";
import { formatSummaryDuration } from "web-apps/company/utils/timeAndDate";
import { type TimeReportDto } from "model/TimeReport";
import {
  type AnalyticsPeriod,
  type OperatingHours,
  isOrderInBusinessDay,
  isOrderInBusinessWindow,
  getWeekBounds,
  walkTimeReportSlots,
} from "./analytics.operatingHours";

// ─── Personnel cost ───────────────────────────────────────────────────────────

/**
 * Walks each time report hour-by-hour and sums hourlyRate × hours only for
 * slots that fall within the operating window. The result is then scaled by
 * the API's cost multiplier to produce the accurate stat card value.
 */
export const computeBusinessHourGross = (
  reports: TimeReportDto[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): number => {
  let gross = 0;
  for (const report of reports) {
    walkTimeReportSlots(report, (slotStart, fractionHours) => {
      const inBusiness =
        period === "day"
          ? isOrderInBusinessDay(slotStart, selectedDate, operatingHours)
          : isOrderInBusinessWindow(slotStart, operatingHours);
      if (inBusiness) {
        gross += report.hourlyRate * fractionHours;
      }
    });
  }
  return gross;
};

// ─── Date / period utilities ──────────────────────────────────────────────────

export const getDateRange = (
  date: LocalDate,
  period: AnalyticsPeriod,
): { fromDate: LocalDate; toDate: LocalDate } => {
  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(date);
    return { fromDate: weekStart, toDate: weekEnd };
  }
  return { fromDate: date, toDate: date };
};

export const toYearMonth = (date: LocalDate): number =>
  parseInt(`${date.year()}${String(date.monthValue()).padStart(2, "0")}`);

/**
 * Returns true when the selected period contains at least one overnight window
 * (closeHour < openHour), meaning the API date range must be extended by one day.
 */
export const isOvernightActive = (
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
  operatingHours: OperatingHours,
): boolean => {
  if (period === "day") {
    const idx = selectedDate.dayOfWeek().value() - 1;
    const dh = operatingHours[idx];
    return dh.enabled && dh.closeHour < dh.openHour;
  }
  return operatingHours.some((dh) => dh.enabled && dh.closeHour < dh.openHour);
};

export const formatDateLabel = (
  date: LocalDate,
  period: AnalyticsPeriod,
  todayLabel: string,
  yesterdayLabel: string,
): string => {
  if (period === "week") {
    const { weekStart, weekEnd } = getWeekBounds(date);
    const fmt = (d: LocalDate) =>
      `${String(d.dayOfMonth()).padStart(2, "0")}/${String(d.monthValue()).padStart(2, "0")}`;
    return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
  }
  const today = LocalDate.now();
  if (date.isEqual(today)) return todayLabel;
  if (date.isEqual(today.minusDays(1))) return yesterdayLabel;
  return `${String(date.dayOfMonth()).padStart(2, "0")}/${String(date.monthValue()).padStart(2, "0")}`;
};

// ─── Formatting ───────────────────────────────────────────────────────────────

export const formatCost = (amount: number): string =>
  `${Math.round(amount).toLocaleString("sv-SE")} kr`;

// ─── Card order ───────────────────────────────────────────────────────────────

export const CARD_ORDER_STORAGE_KEY = "analytics_card_order";

export const DEFAULT_CARD_ORDER = [
  "sales",
  "customEarnings",
  "personnelCost",
  "customCosts",
  "personnelCostPct",
  "plannedHours",
  "salesPerHour",
  "tips",
  "result",
];

export const initCardOrder = (): string[] => {
  try {
    const stored = localStorage.getItem(CARD_ORDER_STORAGE_KEY);
    if (stored) {
      const parsed: string[] = JSON.parse(stored);
      const valid = parsed.filter((l) => DEFAULT_CARD_ORDER.includes(l));
      const newCards = DEFAULT_CARD_ORDER.filter((l) => !parsed.includes(l));
      return [...valid, ...newCards];
    }
  } catch {}
  return [...DEFAULT_CARD_ORDER];
};

/** Returns a new key array with the dragged card moved to the drop position. */
export const reorderCards = (
  orderedKeys: string[],
  dragIndex: number,
  dropSlot: number,
): string[] => {
  const newOrder = [...orderedKeys];
  const adjustedSlot = dragIndex < dropSlot ? dropSlot - 1 : dropSlot;
  const [moved] = newOrder.splice(dragIndex, 1);
  newOrder.splice(adjustedSlot, 0, moved);
  return newOrder;
};

// ─── Stat cards ───────────────────────────────────────────────────────────────

export interface StatCardData {
  key: string;
  label: string;
  value: string;
  isLoading: boolean;
  forecast: string;
}

/** Translated display labels for each stat card — resolved by the component. */
export interface StatCardLabels {
  sales: string;
  customEarnings: string;
  personnelCost: string;
  customCosts: string;
  personnelCostPct: string;
  plannedHours: string;
  salesPerHour: string;
  tips: string;
  result: string;
}

export interface BuildStatCardsParams {
  labels: StatCardLabels;
  sales: number | null;
  tips: number | null;
  personnelCost: number | null;
  customEarningsTotal: number;
  customCostsTotal: number;
  summary: GetTimeReportsV2SummaryResponse | null;
  operatingHours: OperatingHours;
  period: AnalyticsPeriod;
  selectedDate: LocalDate;
  ordersLoading: boolean;
  personnelCostLoading: boolean;
  summaryLoading: boolean;
}

/** Sorts stat cards according to the user's saved card order. */
export const sortStatCards = (
  stats: StatCardData[],
  cardOrder: string[],
): StatCardData[] =>
  [...stats].sort((a, b) => {
    const ai = cardOrder.indexOf(a.key);
    const bi = cardOrder.indexOf(b.key);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

// ── Private card builders ─────────────────────────────────────────────────────

const computeOperatingHoursDuration = (
  operatingHours: OperatingHours,
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
): number => {
  const dayDuration = (idx: number): number => {
    const dh = operatingHours[idx];
    if (!dh.enabled) return 0;
    // e.g. open=10 close=23 → 13 h; open=22 close=3 → 5 h (overnight)
    return dh.openHour === dh.closeHour
      ? 24
      : (dh.closeHour - dh.openHour + 24) % 24;
  };
  if (period === "day") {
    // joda DayOfWeek.value(): 1=Mon…7=Sun → index 0–6
    return dayDuration(selectedDate.dayOfWeek().value() - 1);
  }
  return operatingHours.reduce((sum, _, i) => sum + dayDuration(i), 0);
};

const buildPlannedHoursCard = (
  label: string,
  summary: GetTimeReportsV2SummaryResponse | null,
  isLoading: boolean,
): StatCardData => ({
  key: "plannedHours",
  label,
  value: summary ? formatSummaryDuration(summary.planned.time) : "–",
  isLoading,
  forecast: "–",
});

const buildPersonnelCostCard = (
  label: string,
  cost: number | null,
  isLoading: boolean,
): StatCardData => ({
  key: "personnelCost",
  label,
  value: cost !== null ? formatCost(cost) : "–",
  isLoading,
  forecast: "–",
});

// ── Main entry point ──────────────────────────────────────────────────────────

export const buildStatCards = (params: BuildStatCardsParams): StatCardData[] => {
  const {
    labels,
    sales,
    tips,
    personnelCost,
    customEarningsTotal,
    customCostsTotal,
    summary,
    operatingHours,
    period,
    selectedDate,
    ordersLoading,
    personnelCostLoading,
    summaryLoading,
  } = params;

  const personnelCostPct =
    personnelCost !== null && sales !== null && sales > 0
      ? (personnelCost / sales) * 100
      : null;

  const operatingHoursDuration = computeOperatingHoursDuration(
    operatingHours,
    period,
    selectedDate,
  );

  const salesPerHour =
    sales !== null && operatingHoursDuration > 0
      ? sales / operatingHoursDuration
      : null;

  return [
    {
      key: "sales",
      label: labels.sales,
      value: sales !== null ? formatCost(sales) : "–",
      isLoading: ordersLoading,
      forecast: "–",
    },
    {
      key: "customEarnings",
      label: labels.customEarnings,
      value: formatCost(customEarningsTotal),
      isLoading: false,
      forecast: "–",
    },
    buildPersonnelCostCard(labels.personnelCost, personnelCost, personnelCostLoading),
    {
      key: "customCosts",
      label: labels.customCosts,
      value: formatCost(customCostsTotal),
      isLoading: false,
      forecast: "–",
    },
    {
      key: "personnelCostPct",
      label: labels.personnelCostPct,
      value: personnelCostPct !== null ? `${personnelCostPct.toFixed(1)}%` : "–",
      isLoading: ordersLoading || personnelCostLoading,
      forecast: "–",
    },
    buildPlannedHoursCard(labels.plannedHours, summary, summaryLoading),
    {
      key: "salesPerHour",
      label: labels.salesPerHour,
      value: salesPerHour !== null ? formatCost(salesPerHour) : "–",
      isLoading: ordersLoading,
      forecast: "–",
    },
    {
      key: "tips",
      label: labels.tips,
      value: tips !== null ? formatCost(tips) : "–",
      isLoading: ordersLoading,
      forecast: "–",
    },
    {
      key: "result",
      label: labels.result,
      value:
        sales !== null && personnelCost !== null
          ? formatCost(
              sales + customEarningsTotal - personnelCost - customCostsTotal,
            )
          : "–",
      isLoading: ordersLoading || personnelCostLoading,
      forecast: "–",
    },
  ];
};
